import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import MathText from './Math';

export interface VariableOption {
  value: string;
  /** Full label, may embed `$<latex>$` spans — rendered via <MathText>. */
  label: string;
}

export interface VariableOptionGroup {
  /** Group header text. Omit/empty to render options with no header (flat list). */
  label?: string;
  options: VariableOption[];

}

interface VariableSelectProps {
  value: string;
  onChange: (value: string) => void;
  groups: VariableOptionGroup[];
  minWidth?: string;
  ariaLabel?: string;
}

/**
 * Custom dropdown replacing the native <select> for variable pickers.
 * Native <option> cannot render HTML/math, so this renders each row via
 * <MathText> instead. Follows the app's hand-rolled-control precedent
 * (shared/MultiSelect.tsx): inline styles object + CSS custom properties,
 * no external UI library.
 */
const VariableSelect: React.FC<VariableSelectProps> = ({
  value,
  onChange,
  groups,
  minWidth = '260px',
  ariaLabel = 'Variable',
}) => {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const optionRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Flatten for keyboard navigation + finding the selected option's label.
  const flatOptions = useMemo(() => groups.flatMap(g => g.options), [groups]);
  const selectedOption = useMemo(
    () => flatOptions.find(o => o.value === value),
    [flatOptions, value]
  );

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Reset keyboard highlight to the current selection whenever the panel opens,
  // and scroll it into view.
  useEffect(() => {
    if (!open) return;
    const idx = flatOptions.findIndex(o => o.value === value);
    const start = idx >= 0 ? idx : 0;
    setHighlightIndex(start);
    optionRefs.current[start]?.scrollIntoView({ block: 'nearest' });
  }, [open, flatOptions, value]);

  useEffect(() => {
    if (open && highlightIndex >= 0) {
      optionRefs.current[highlightIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, open]);

  const commitSelection = useCallback(
    (v: string) => {
      onChange(v);
      setOpen(false);
    },
    [onChange]
  );

  const handleTriggerKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  const handlePanelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex(i => Math.min(i + 1, flatOptions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const opt = flatOptions[highlightIndex];
        if (opt) commitSelection(opt.value);
      } else if (e.key === 'Tab') {
        setOpen(false);
      }
    },
    [flatOptions, highlightIndex, commitSelection]
  );

  let flatCursor = -1;

  const renderGroup = (group: VariableOptionGroup, gi: number | string) => (
    <div key={group.label ?? gi}>
      {group.label && (
        <div style={styles.groupHeader}>
          {group.label}
        </div>
      )}
      {group.options.map(opt => {
        flatCursor += 1;
        const idx = flatCursor;
        const isSelected = opt.value === value;
        const isHighlighted = idx === highlightIndex;
        return (
          <div
            key={opt.value}
            ref={el => { optionRefs.current[idx] = el; }}
            role="option"
            aria-selected={isSelected}
            style={{
              ...styles.option,
              ...(isSelected ? styles.optionSelected : {}),
              ...(isHighlighted ? styles.optionHighlighted : {}),
            }}
            onMouseEnter={() => setHighlightIndex(idx)}
            onClick={() => commitSelection(opt.value)}
          >
            <span style={styles.optionLabel}>
              <MathText text={opt.label} />
            </span>
            {isSelected && <Check size={14} style={{ flexShrink: 0, color: 'var(--color-primary)' }} />}
          </div>
        );
      })}
    </div>
  );

  return (
    <div ref={containerRef} style={{ ...styles.container, minWidth }}>
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={ariaLabel}
        style={styles.trigger}
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleTriggerKeyDown}
      >
        <span style={styles.triggerLabel}>
          {selectedOption ? <MathText text={selectedOption.label} /> : <span style={styles.placeholder}>Select a variable…</span>}
        </span>
        <ChevronDown size={16} style={{ flexShrink: 0, opacity: 0.6 }} />
      </button>

      {open && (
        <div
          id={panelId}
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={-1}
          style={styles.panel}
          onKeyDown={handlePanelKeyDown}
          ref={el => el?.focus()}
        >
          {groups.map((group, gi) => renderGroup(group, gi))}
          {flatOptions.length === 0 && <div style={styles.emptyState}>No variables available</div>}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'relative',
    display: 'inline-block',
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
    padding: '6px 10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: 'var(--color-text)',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    textAlign: 'left',
  },
  triggerLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  placeholder: {
    color: 'var(--color-text-subtle)',
  },
  panel: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    zIndex: 50,
    minWidth: '100%',
    width: 'max-content',
    maxWidth: '520px',
    maxHeight: '360px',
    overflowY: 'auto',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    padding: '4px 0',
  },
  groupHeader: {
    padding: '6px 12px 4px',
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: 'var(--color-text-subtle)',
    backgroundColor: 'var(--color-bg)',
    position: 'sticky',
    top: 0,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '6px 12px',
    fontSize: '14px',
    color: 'var(--color-text)',
    cursor: 'pointer',
  },
  optionLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  optionSelected: {
    color: 'var(--color-primary)',
    fontWeight: 600,
  },
  optionHighlighted: {
    backgroundColor: 'var(--color-bg)',
  },
  emptyState: {
    padding: '12px',
    fontSize: '14px',
    color: 'var(--color-text-subtle)',
    textAlign: 'center',
  },
};

export default VariableSelect;
